import { supabase } from './supabaseClient';

const POSTHOG_PROJECT_ID = '280011';
const CACHE_TTL_MS = 10 * 60 * 1000; // 10 minutes
const cache = new Map();

// ── Global filter: exclude VibrantCast staff from all queries ────

const INTERNAL_USERS_COHORT_ID = 220647;

const EXCLUDE_INTERNAL_USERS = [
  { type: 'cohort', key: 'id', value: INTERNAL_USERS_COHORT_ID, operator: 'not_in' },
];

// ── Query builders ──────────────────────────────────────────────

export function buildTrendsQuery(events, dateFrom, options = {}) {
  const { interval = 'day', breakdownBy } = options;

  const series = events.map((e) => {
    if (typeof e === 'string') {
      return { kind: 'EventsNode', event: e, math: 'total' };
    }
    return { kind: 'EventsNode', event: e.event, math: e.math || 'total' };
  });

  const query = {
    kind: 'TrendsQuery',
    series,
    dateRange: { date_from: dateFrom },
    interval,
    properties: EXCLUDE_INTERNAL_USERS,
  };

  if (breakdownBy) {
    query.breakdownFilter = breakdownBy;
  }

  return { query };
}

export function buildFunnelsQuery(events, dateFrom, options = {}) {
  const { windowDays = 14 } = options;

  return {
    query: {
      kind: 'FunnelsQuery',
      series: events.map((event) => ({
        kind: 'EventsNode',
        event,
      })),
      dateRange: { date_from: dateFrom },
      funnelsFilter: {
        funnelWindowInterval: windowDays,
        funnelWindowIntervalUnit: 'day',
      },
      properties: EXCLUDE_INTERNAL_USERS,
    },
  };
}

// ── Cache ────────────────────────────────────────────────────────

function cacheKey(payload) {
  return JSON.stringify(payload);
}

export function clearCache() {
  cache.clear();
}

// ── Fetch ────────────────────────────────────────────────────────

export async function queryPostHog(queryPayload, options = {}) {
  const {
    skipCache = false,
    endpoint = 'query',
    method = 'POST',
    params,
  } = options;

  const key = cacheKey({ queryPayload, endpoint, params });

  if (!skipCache) {
    const cached = cache.get(key);
    if (cached && Date.now() - cached.timestamp < CACHE_TTL_MS) {
      return cached.data;
    }
  }

  const pathMap = {
    query: `/api/projects/${POSTHOG_PROJECT_ID}/query/`,
    error_tracking: `/api/projects/${POSTHOG_PROJECT_ID}/error_tracking/issues/`,
  };

  const path = pathMap[endpoint];
  if (!path) {
    throw new Error(`Unknown endpoint: ${endpoint}`);
  }

  const { data, error } = await supabase.functions.invoke(
    'posthog_query_proxy',
    {
      body: {
        method,
        path,
        ...(method === 'POST' ? { body: queryPayload } : {}),
        ...(params ? { params } : {}),
      },
    }
  );

  if (error) {
    throw new Error(`PostHog query failed: ${error.message}`);
  }

  cache.set(key, { data, timestamp: Date.now() });
  return data;
}
